// packages/shared/src/utils/availabilityEngine.ts
//
// THE availability engine. Every consumer that needs to know "which times are
// bookable for this provider under these constraints" calls this module:
//   - appointment wizard: slot generation, available-time lists, add/adjust slot
//   - provider eligibility (client-side, from raw rows)
//   - calendar waitlist matching (stripes)
//   - duplicate-appointment modal
//
// Override semantics (schedule_override.type) are decided HERE, once:
//   unavailable : hard block (whole day when no blocks)
//   note        : soft block. Excluded from suggestion, time lists and matching
//                 (whole day when no blocks); a deliberate manual placement is
//                 allowed and surfaces as outside-hours with the note text.
//   open        : additive availability
//   custom      : replaces the day's template blocks
//
// Time preferences are a list of windows plus excluded weekdays. A slot
// satisfies a window when the WHOLE appointment fits inside it; a window whose
// end is null means "start exactly at start". Windows are a union.
//
// Pure: no I/O, no React. Dates are YYYY-MM-DD strings, times are HH:mm strings
// or minutes since midnight. "Today" is the caller's local date.

import { resolveScheduleTemplate } from './scheduleTemplateResolver';

// ============ Types ============

export interface TimeWindow {
  start: number;          // minutes since midnight
  end: number | null;     // null = must start exactly at `start`
}

export interface SchedulingConstraints {
  windows: TimeWindow[];
  excludedDays: number[]; // 0=Sun..6=Sat
}

export interface TimeWindowPreset extends TimeWindow {
  id: string;
  label: string;
}

export interface MinuteRange {
  start: number;
  end: number;
}

export interface AvailabilityOptions {
  locationId?: string | null;
  holidayDates?: Set<string> | null;
}

export interface SchedulingWindow {
  displayStartDate: string;   // Sunday the grid starts on
  earliestSlotDate: string;   // first date a slot may land on
}

// ============ Presets ============

// Single source for the preset chips (wizard Provider step + waitlist form).
// "Before 8am" means the appointment ENDS by 8:00 (containment rule).
export const TIME_WINDOW_PRESETS: TimeWindowPreset[] = [
  { id: 'before_8am', label: 'Before 8am', start: 0, end: 480 },
  { id: '11am_4pm', label: '11am to 4pm', start: 660, end: 960 },
  { id: '4pm_onward', label: '4pm onward', start: 960, end: 1440 },
];

export function windowsEqual(a: TimeWindow | null | undefined, b: TimeWindow | null | undefined): boolean {
  if (!a || !b) return false;
  return a.start === b.start && (a.end ?? null) === (b.end ?? null);
}

export function isPresetWindow(w: TimeWindow): boolean {
  return TIME_WINDOW_PRESETS.some(p => windowsEqual(p, w));
}

// ============ Time helpers ============

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60).toString().padStart(2, '0');
  const m = (mins % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

/** 12-hour display, e.g. 600 -> "10:00 AM". */
export function formatMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 && h < 24 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

export function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function getDayOfWeek(dateStr: string): number {
  return new Date(dateStr + 'T12:00:00').getDay();
}

/** Local calendar date of `now` (default: the current moment) as YYYY-MM-DD. */
export function localDateString(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function sundayOfWeek(dateStr: string): string {
  return addDays(dateStr, -getDayOfWeek(dateStr));
}

export function rangesOverlap(startA: number, endA: number, startB: number, endB: number): boolean {
  return startA < endB && startB < endA;
}

// ============ Time preferences ============

const DAY_MINUTES = 24 * 60;

function isFiniteMinute(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Normalize any stored/in-memory time preference shape to constraints.
 *
 * New shape:    { windows: [{start, end|null}], excluded_days: [] }
 * Legacy shape: { time_restrictions: [{filter:{start_before|start_from|end_by}}],
 *                 specific_time: minutes, excluded_days: [] }
 *   start_before X            -> {0, X}
 *   start_from A + end_by B   -> {A, B}
 *   start_from A              -> {A, 1440}
 *   end_by B                  -> {0, B}
 *   specific_time t           -> {t, null}, and legacy restrictions are dropped
 *                                (specific time always had precedence)
 * null / undefined / {}       -> no windows, no excluded days (unrestricted)
 */
export function normalizeTimePreferences(prefs: any): SchedulingConstraints {
  const excludedDays: number[] = Array.isArray(prefs?.excluded_days)
    ? prefs.excluded_days.filter((d: unknown) => isFiniteMinute(d) && d >= 0 && d <= 6)
    : Array.isArray(prefs?.excludedDays)
      ? prefs.excludedDays.filter((d: unknown) => isFiniteMinute(d) && d >= 0 && d <= 6)
      : [];

  if (!prefs) return { windows: [], excludedDays };

  const windows: TimeWindow[] = [];

  if (Array.isArray(prefs.windows)) {
    for (const w of prefs.windows) {
      if (!w || !isFiniteMinute(w.start)) continue;
      const end = w.end == null ? null : (isFiniteMinute(w.end) ? w.end : null);
      if (end != null && end <= w.start) continue;
      windows.push({ start: w.start, end });
    }
    return { windows: dedupeWindows(windows), excludedDays };
  }

  if (isFiniteMinute(prefs.specific_time)) {
    return { windows: [{ start: prefs.specific_time, end: null }], excludedDays };
  }

  if (Array.isArray(prefs.time_restrictions)) {
    for (const r of prefs.time_restrictions) {
      const f = r?.filter || {};
      if (isFiniteMinute(f.start_before)) {
        windows.push({ start: 0, end: f.start_before });
      } else if (isFiniteMinute(f.start_from) || isFiniteMinute(f.end_by)) {
        const start = isFiniteMinute(f.start_from) ? f.start_from : 0;
        const end = isFiniteMinute(f.end_by) ? f.end_by : DAY_MINUTES;
        if (end > start) windows.push({ start, end });
      }
    }
  }

  return { windows: dedupeWindows(windows), excludedDays };
}

function dedupeWindows(windows: TimeWindow[]): TimeWindow[] {
  const out: TimeWindow[] = [];
  for (const w of windows) {
    if (!out.some(o => windowsEqual(o, w))) out.push(w);
  }
  return out;
}

/** The stored shape written by every writer (waitlist form). */
export function serializeTimePreferences(constraints: SchedulingConstraints): { windows: TimeWindow[]; excluded_days: number[] } {
  return {
    windows: (constraints?.windows || []).map(w => ({ start: w.start, end: w.end ?? null })),
    excluded_days: [...(constraints?.excludedDays || [])],
  };
}

export function hasTimePreferences(constraints: SchedulingConstraints | null | undefined): boolean {
  return !!constraints && ((constraints.windows?.length || 0) > 0 || (constraints.excludedDays?.length || 0) > 0);
}

/** Human text for one window. Labels are derived, never stored. */
export function formatTimeWindow(w: TimeWindow): string {
  if (w.end == null) return `At ${formatMinutes(w.start)}`;
  if (w.start <= 0 && w.end < DAY_MINUTES) return `Before ${formatMinutes(w.end)}`;
  if (w.end >= DAY_MINUTES) return `${formatMinutes(w.start)} onward`;
  return `${formatMinutes(w.start)} to ${formatMinutes(w.end)}`;
}

export function isSlotAllowedByConstraints(
  startMinutes: number,
  endMinutes: number,
  date: string,
  constraints: SchedulingConstraints | null | undefined
): boolean {
  const windows = constraints?.windows || [];
  const excludedDays = constraints?.excludedDays || [];
  if (excludedDays.length > 0 && excludedDays.includes(getDayOfWeek(date))) return false;
  if (windows.length === 0) return true;
  return windows.some(w => {
    if (startMinutes < w.start) return false;
    if (w.end == null) return startMinutes === w.start;
    return endMinutes <= w.end;
  });
}

// ============ Calendar notes (text lookup only) ============

/**
 * Index note text by date. Blocking is handled in buildAvailabilityMap; this
 * index only supplies the note text shown on a manually placed slot.
 * A note with no blocks applies to the whole day.
 */
export function buildNotesByDate(scheduleOverrides: any[]): Map<string, { start: number; end: number; reason: string }[]> {
  const notesByDate = new Map<string, { start: number; end: number; reason: string }[]>();
  for (const ov of (scheduleOverrides || [])) {
    if (!ov || ov.type !== 'note') continue;
    const reason = (ov.reason || '').trim();
    if (!reason) continue;
    const ranges = noteRanges(ov).map(r => ({ ...r, reason }));
    if (!notesByDate.has(ov.date)) notesByDate.set(ov.date, []);
    notesByDate.get(ov.date)!.push(...ranges);
  }
  return notesByDate;
}

export function getNoteForRange(
  notesByDate: Map<string, { start: number; end: number; reason: string }[]> | null | undefined,
  date: string,
  startMins: number,
  endMins: number
): string | null {
  const notes = notesByDate?.get(date);
  if (!notes || notes.length === 0) return null;
  const hits: string[] = [];
  for (const n of notes) {
    if (rangesOverlap(startMins, endMins, n.start, n.end) && !hits.includes(n.reason)) hits.push(n.reason);
  }
  return hits.length > 0 ? hits.join('; ') : null;
}

export function getNoteForSlot(scheduleOverrides: any[], date: string, startTime: string, endTime: string): string | null {
  if (!startTime || !endTime) return null;
  return getNoteForRange(buildNotesByDate(scheduleOverrides), date, timeToMinutes(startTime), timeToMinutes(endTime));
}

// The minute ranges a note covers. No usable blocks = the whole day, which is
// exactly how the calendar column draws a blockless note.
function noteRanges(ov: any): MinuteRange[] {
  const blocks = Array.isArray(ov?.blocks) ? ov.blocks.filter((b: any) => b && b.start_time && b.end_time) : [];
  if (blocks.length === 0) return [{ start: 0, end: DAY_MINUTES }];
  return blocks.map((b: any) => ({ start: timeToMinutes(b.start_time), end: timeToMinutes(b.end_time) }));
}

// ============ Availability map ============

export function subtractRanges(blocks: MinuteRange[], ranges: MinuteRange[]): MinuteRange[] {
  let result = [...blocks];
  for (const range of ranges) {
    result = result.flatMap(b => {
      if (!rangesOverlap(b.start, b.end, range.start, range.end)) return [b];
      const parts: MinuteRange[] = [];
      if (b.start < range.start) parts.push({ start: b.start, end: range.start });
      if (b.end > range.end) parts.push({ start: range.end, end: b.end });
      return parts;
    });
  }
  return result;
}

export function subtractAppointments(blocks: MinuteRange[], appointments: any[]): MinuteRange[] {
  const ranges: MinuteRange[] = [];
  for (const appt of (appointments || [])) {
    if (!appt || !appt.start_time || !appt.end_time) continue;
    ranges.push({ start: timeToMinutes(appt.start_time), end: timeToMinutes(appt.end_time) });
  }
  return subtractRanges(blocks, ranges);
}

/**
 * Available blocks per date for the window.
 * Rows: provider_availability records (weekly templates, scheduled changes,
 * history). Overrides: schedule_override rows for the window.
 */
export function buildAvailabilityMap(
  providerAvailability: any[],
  scheduleOverrides: any[],
  startDate: string,
  totalDays: number,
  options: AvailabilityOptions = {}
): Map<string, MinuteRange[]> {
  const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const map = new Map<string, MinuteRange[]>();
  const overridesByDate = new Map<string, any[]>();

  // Only active templates; when a location is selected only records for that
  // location. Location-less legacy rows never bleed into another location.
  const { locationId, holidayDates } = options;
  const activeAvailability = (providerAvailability || []).filter(r =>
    r && r.is_active === true && (!locationId || r.location_id === locationId)
  );

  for (const ov of (scheduleOverrides || [])) {
    if (!ov) continue;
    if (!overridesByDate.has(ov.date)) overridesByDate.set(ov.date, []);
    overridesByDate.get(ov.date)!.push(ov);
  }

  for (let i = 0; i < totalDays; i++) {
    const date = addDays(startDate, i);
    const dayName = DAY_NAMES[getDayOfWeek(date)];
    const dateOverrides = overridesByDate.get(date) || [];

    // Company holidays block the entire day org-wide
    if (holidayDates && holidayDates.has(date)) {
      map.set(date, []);
      continue;
    }

    // unavailable with no blocks -> whole day blocked
    const dayBlocked = dateOverrides.some(o =>
      o.type === 'unavailable' && !(Array.isArray(o.blocks) && o.blocks.length > 0)
    );
    if (dayBlocked) {
      map.set(date, []);
      continue;
    }

    // Template blocks for this weekday (past / current / pending template)
    let blocks: MinuteRange[] = [];
    for (const record of activeAvailability) {
      const template: any = resolveScheduleTemplate(record, date);
      if (!template) continue;
      if (template.effective_from && date < template.effective_from) continue;
      if (template.effective_until && date > template.effective_until) continue;
      const daySchedule = template[dayName];
      if (!daySchedule || !daySchedule.is_available) continue;
      for (const block of (daySchedule.blocks || [])) {
        if (block.start_time && block.end_time) {
          blocks.push({ start: timeToMinutes(block.start_time), end: timeToMinutes(block.end_time) });
        }
      }
    }

    // open = additive, custom = replaces the day's blocks
    for (const ov of dateOverrides) {
      if (!Array.isArray(ov.blocks)) continue;
      if (ov.type === 'open') {
        for (const b of ov.blocks) {
          if (b.start_time && b.end_time) {
            blocks.push({ start: timeToMinutes(b.start_time), end: timeToMinutes(b.end_time) });
          }
        }
      } else if (ov.type === 'custom') {
        blocks = ov.blocks
          .filter((b: any) => b.start_time && b.end_time)
          .map((b: any) => ({ start: timeToMinutes(b.start_time), end: timeToMinutes(b.end_time) }));
      }
    }

    // Subtract timed unavailable blocks and every note (runs AFTER custom
    // replacement so a note survives a same-day custom override)
    const toSubtract: MinuteRange[] = [];
    for (const ov of dateOverrides) {
      if (ov.type === 'unavailable') {
        for (const b of (ov.blocks || [])) {
          if (b.start_time && b.end_time) {
            toSubtract.push({ start: timeToMinutes(b.start_time), end: timeToMinutes(b.end_time) });
          }
        }
      } else if (ov.type === 'note') {
        toSubtract.push(...noteRanges(ov));
      }
    }
    if (toSubtract.length > 0) blocks = subtractRanges(blocks, toSubtract);

    // Sort and merge overlapping blocks
    blocks.sort((a, b) => a.start - b.start);
    if (blocks.length > 1) {
      const merged = [blocks[0]];
      for (let j = 1; j < blocks.length; j++) {
        const prev = merged[merged.length - 1];
        if (blocks[j].start <= prev.end) {
          prev.end = Math.max(prev.end, blocks[j].end);
        } else {
          merged.push(blocks[j]);
        }
      }
      blocks = merged;
    }
    map.set(date, blocks);
  }

  return map;
}

/** True when [startTime, endTime] sits inside the provider's availability (notes excluded). */
export function isTimeWithinProviderHours(
  date: string,
  startTime: string,
  endTime: string,
  providerAvailability: any[],
  scheduleOverrides: any[],
  options: AvailabilityOptions = {}
): boolean {
  const availMap = buildAvailabilityMap(providerAvailability, scheduleOverrides, date, 1, options);
  const blocks = availMap.get(date) || [];
  const slotStart = timeToMinutes(startTime);
  const slotEnd = timeToMinutes(endTime);
  return blocks.some(block => slotStart >= block.start && slotEnd <= block.end);
}

// ============ Candidates ============

/**
 * Candidate start minutes for a duration inside free blocks: the duration
 * cadence from each block start (packs back-to-back after existing
 * appointments) PLUS every anchor that fits inside a free block, so a window
 * start such as 10:00 is offered even when the cadence would skip it.
 * Deduped and ascending: tie-breaks in the scorer rely on earliest-first order.
 */
export function findCandidateStarts(freeBlocks: MinuteRange[], durationMinutes: number, anchors: number[] = []): number[] {
  const starts = new Set<number>();
  const step = durationMinutes > 0 ? durationMinutes : 15;
  for (const block of freeBlocks) {
    for (let t = block.start; t + durationMinutes <= block.end; t += step) {
      starts.add(t);
    }
    for (const a of anchors) {
      if (a >= block.start && a + durationMinutes <= block.end) starts.add(a);
    }
  }
  return [...starts].sort((a, b) => a - b);
}

export function windowAnchors(constraints: SchedulingConstraints | null | undefined): number[] {
  return (constraints?.windows || []).map(w => w.start);
}

// ============ Scoring ============

function scoreSlot(startMinutes: number, date: string, context: any): number {
  let score = 0.5;
  const dow = getDayOfWeek(date);

  // Consistency: same day-of-week as previous weeks
  if (context.previousDays.length > 0) {
    score += context.previousDays.includes(dow) ? 0.2 : 0;
  }

  // Consistency: same time-of-day as previous weeks
  if (context.previousTimes.length > 0) {
    const minDiff = Math.min(...context.previousTimes.map((t: number) => Math.abs(t - startMinutes)));
    if (minDiff === 0) score += 0.2;
    else if (minDiff <= 30) score += 0.1;
    else if (minDiff <= 60) score += 0.05;
  }

  // No patient conflict
  if (context.sameDayPatientAppts.length === 0) score += 0.05;

  // Even spacing within the week
  if (context.scheduledDatesThisWeek.length > 0 && context.minDaysBetween > 0) {
    const scheduledDows = context.scheduledDatesThisWeek.map((d: string) => getDayOfWeek(d));
    const minGap = Math.min(...scheduledDows.map((d: number) => {
      const gap = Math.abs(dow - d);
      return Math.min(gap, 7 - gap);
    }));
    if (minGap >= context.minDaysBetween) score += 0.05;
  }

  // Prefer spreading visits across days: a day already used this week loses
  // more than any consistency bonus can recover, but stays eligible when a
  // zero-gap rule leaves no other day.
  if (context.scheduledDatesThisWeek.includes(date)) score -= 0.1;

  return Math.min(score, 1);
}

export function getPatternForVisit(visitPattern: any[], visitNumber: number): any {
  const direct = visitPattern.find(p => p.visit_number === visitNumber);
  if (direct) return direct;
  const defaultPattern = visitPattern.find(p => p.is_default);
  if (defaultPattern) return defaultPattern;
  return visitPattern[visitPattern.length - 1];
}

// ============ Generation ============

/**
 * Suggested appointment slots across a multi-week window.
 *
 * options.startingVisitNumber   offset for visit numbering
 * options.existingScheduledDates dates already scheduled (spacing)
 * options.earliestSlotDate      first eligible date
 * options.locationId / holidayDates as buildAvailabilityMap
 * options.now                   injectable clock (tests)
 */
export function generateSuggestedSlots(
  rule: any,
  providerAvailability: any[],
  providerAppointments: any[],
  patientAppointments: any[],
  startDate: string,
  weeksOut: number,
  scheduleOverrides: any[] = [],
  options: any = {},
  constraints: SchedulingConstraints | null = null
): any[] {
  const { startingVisitNumber = 0, existingScheduledDates = [], earliestSlotDate, locationId, holidayDates } = options;
  const totalDays = weeksOut * 7;
  const availabilityMap = buildAvailabilityMap(providerAvailability, scheduleOverrides, startDate, totalDays, { locationId, holidayDates });
  const notesByDate = buildNotesByDate(scheduleOverrides);
  const anchors = windowAnchors(constraints);

  // Provider appointments by date; placed suggestions are appended here so
  // later visits never overlap earlier ones.
  const providerApptsByDate = new Map<string, any[]>();
  for (const appt of (providerAppointments || [])) {
    const d = appt.appointment_date;
    if (!providerApptsByDate.has(d)) providerApptsByDate.set(d, []);
    providerApptsByDate.get(d)!.push(appt);
  }

  const patientApptsByDate = new Map<string, any[]>();
  for (const appt of (patientAppointments || [])) {
    const d = appt.appointment_date || appt.date;
    if (!patientApptsByDate.has(d)) patientApptsByDate.set(d, []);
    patientApptsByDate.get(d)!.push(appt);
  }

  const now: Date = options.now || new Date();
  const todayStr = localDateString(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const suggestedSlots: any[] = [];
  let globalVisitNumber = startingVisitNumber;

  const previousTimes: number[] = [];
  const previousDays: number[] = [];
  const allScheduledDates: string[] = [...existingScheduledDates];

  for (let week = 0; week < weeksOut; week++) {
    const weekStartDate = addDays(startDate, week * 7);
    const scheduledDatesThisWeek: string[] = [];
    const weekDays: number[] = [];
    const weekTimes: number[] = [];

    for (let visit = 0; visit < rule.visits_per_week; visit++) {
      globalVisitNumber++;
      const pattern = getPatternForVisit(rule.visit_pattern, globalVisitNumber);
      const duration = pattern.duration_minutes;

      let bestSlot: any = null;
      let bestScore = -1;
      const alternatives: any[] = [];

      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const date = addDays(weekStartDate, dayOffset);

        if (date < todayStr) continue;
        if (earliestSlotDate && date < earliestSlotDate) continue;
        if (constraints?.excludedDays?.length && constraints.excludedDays.includes(getDayOfWeek(date))) continue;

        // min_days_between_visits against ALL scheduled dates
        if (rule.min_days_between_visits > 0 && allScheduledDates.length > 0) {
          const tooClose = allScheduledDates.some(prevDate => {
            const diffMs = Math.abs(+new Date(date + 'T12:00:00') - +new Date(prevDate + 'T12:00:00'));
            return diffMs / (1000 * 60 * 60 * 24) <= rule.min_days_between_visits;
          });
          if (tooClose) continue;
        }

        const dayBlocks = availabilityMap.get(date) || [];
        const freeBlocks = subtractAppointments(dayBlocks, providerApptsByDate.get(date) || []);
        const patientAppts = patientApptsByDate.get(date) || [];
        const candidates = findCandidateStarts(freeBlocks, duration, anchors);

        for (const startMins of candidates) {
          if (date === todayStr && startMins < nowMinutes) continue;
          const endMins = startMins + duration;
          if (!isSlotAllowedByConstraints(startMins, endMins, date, constraints)) continue;

          const hasPatientConflict = patientAppts.some(pa =>
            rangesOverlap(startMins, endMins, timeToMinutes(pa.start_time), timeToMinutes(pa.end_time))
          );

          const slotScore = scoreSlot(startMins, date, {
            previousTimes,
            previousDays,
            sameDayPatientAppts: hasPatientConflict ? ['conflict'] : [],
            scheduledDatesThisWeek,
            minDaysBetween: rule.min_days_between_visits,
          });

          const slotCandidate = {
            appointment_date: date,
            start_time: minutesToTime(startMins),
            end_time: minutesToTime(endMins),
            score: slotScore,
            note: getNoteForRange(notesByDate, date, startMins, endMins),
          };

          if (slotScore > bestScore) {
            if (bestSlot) alternatives.push({ start_time: bestSlot.start_time, end_time: bestSlot.end_time, note: bestSlot.note });
            bestSlot = slotCandidate;
            bestScore = slotScore;
          } else {
            alternatives.push({ start_time: slotCandidate.start_time, end_time: slotCandidate.end_time, note: slotCandidate.note });
          }
        }
      }

      if (bestSlot) {
        suggestedSlots.push({
          appointment_date: bestSlot.appointment_date,
          start_time: bestSlot.start_time,
          end_time: bestSlot.end_time,
          week_number: week + 1,
          visit_number: globalVisitNumber,
          appointment_type_id: pattern.appointment_type_id,
          appointment_type_name: pattern.appointment_type_name,
          duration_minutes: duration,
          consistency_score: bestScore,
          alternatives: alternatives.slice(0, 5),
          note: bestSlot.note,
        });

        // The placed slot now occupies the provider for later visits
        if (!providerApptsByDate.has(bestSlot.appointment_date)) providerApptsByDate.set(bestSlot.appointment_date, []);
        providerApptsByDate.get(bestSlot.appointment_date)!.push({
          appointment_date: bestSlot.appointment_date,
          start_time: bestSlot.start_time,
          end_time: bestSlot.end_time,
        });

        scheduledDatesThisWeek.push(bestSlot.appointment_date);
        allScheduledDates.push(bestSlot.appointment_date);
        weekTimes.push(timeToMinutes(bestSlot.start_time));
        weekDays.push(getDayOfWeek(bestSlot.appointment_date));
      }
    }

    // Seed consistency only from weeks that placed every expected visit
    if (weekDays.length === rule.visits_per_week) {
      previousDays.push(...weekDays);
      previousTimes.push(...weekTimes);
    }
  }

  return suggestedSlots;
}

/**
 * Every bookable start on one date for a duration, honoring constraints.
 * Notes are excluded (soft block); `note` on each entry is kept for shape
 * stability and is always null.
 */
export function getAvailableTimesForDate(
  date: string,
  providerAvailability: any[],
  providerAppointments: any[],
  scheduleOverrides: any[],
  durationMinutes: number,
  constraints: SchedulingConstraints | null = null,
  options: any = {}
): { start_time: string; end_time: string; note: string | null }[] {
  if (constraints?.excludedDays?.length && constraints.excludedDays.includes(getDayOfWeek(date))) return [];

  const availMap = buildAvailabilityMap(providerAvailability, scheduleOverrides, date, 1, options);
  const blocks = availMap.get(date) || [];
  const dateAppts = (providerAppointments || []).filter(a => a.appointment_date === date);
  const freeBlocks = subtractAppointments(blocks, dateAppts);
  let starts = findCandidateStarts(freeBlocks, durationMinutes, windowAnchors(constraints));

  const now: Date = options.now || new Date();
  if (date === localDateString(now)) {
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    starts = starts.filter(s => s >= nowMinutes);
  }

  starts = starts.filter(s => isSlotAllowedByConstraints(s, s + durationMinutes, date, constraints));

  const notesByDate = buildNotesByDate(scheduleOverrides);
  return starts.map(s => ({
    start_time: minutesToTime(s),
    end_time: minutesToTime(s + durationMinutes),
    note: getNoteForRange(notesByDate, date, s, s + durationMinutes),
  }));
}

export function suggestedToBatchSlots(suggestedSlots: any[], providerId: string, caseId?: number | null, caseName?: string | null): any[] {
  return suggestedSlots.map(slot => ({
    case_id: caseId,
    case_name: caseName,
    provider_id: providerId,
    appointment_date: slot.appointment_date,
    start_time: slot.start_time,
    end_time: slot.end_time,
    duration_minutes: slot.duration_minutes,
    appointment_type_id: slot.appointment_type_id,
    appointment_type_name: slot.appointment_type_name,
    visit_number: slot.visit_number,
    week_number: slot.week_number,
    is_rule_override: false,
    is_outside_hours: false,
    note: slot.note ?? null,
  }));
}

// ============ Scheduling window ============

/**
 * The grid window every rule-mode consumer uses (Provider step eligibility and
 * the Scheduling step draw from the same dates):
 *   - grid starts on the Sunday of the week containing today (or the override)
 *   - the first eligible slot date is today (or the override)
 *   - without an override, existing future patient appointments push both to
 *     the Sunday after the latest one (next Medicare week)
 */
export function computeSchedulingWindow(params: {
  today?: string;
  patientAppointments?: any[];
  startDateOverride?: string | null;
}): SchedulingWindow {
  const today = params.today || localDateString();
  const { startDateOverride } = params;
  let earliestSlotDate = startDateOverride || today;
  let displayStartDate = sundayOfWeek(earliestSlotDate);

  if (!startDateOverride) {
    const future = (params.patientAppointments || [])
      .map(a => a?.appointment_date)
      .filter((d): d is string => typeof d === 'string' && d >= today);
    if (future.length > 0) {
      const latest = future.reduce((a, b) => (b > a ? b : a), future[0]);
      const dow = getDayOfWeek(latest);
      const nextSunday = addDays(latest, dow === 0 ? 7 : 7 - dow);
      if (nextSunday > displayStartDate) {
        displayStartDate = nextSunday;
        earliestSlotDate = nextSunday;
      }
    }
  }

  return { displayStartDate, earliestSlotDate };
}

// ============ Eligibility ============

/**
 * Provider eligibility IS the generator: run it with the same inputs the
 * Scheduling step will use and count what it places.
 */
export function evaluateProviderEligibility(params: {
  providerId: string;
  providerName?: string;
  rule: any;
  weeksOut: number;
  providerAvailability: any[];
  providerAppointments: any[];
  scheduleOverrides: any[];
  patientAppointments?: any[];
  window: SchedulingWindow;
  constraints?: SchedulingConstraints | null;
  options?: AvailabilityOptions & { existingScheduledDates?: string[]; startingVisitNumber?: number; now?: Date };
}): {
  provider_id: string;
  provider_name: string;
  placed_count: number;
  required_count: number;
  coverage_percentage: number;
  is_eligible: boolean;
  reasons: string[];
} {
  const { rule, weeksOut, window } = params;
  const placed = generateSuggestedSlots(
    rule,
    params.providerAvailability || [],
    params.providerAppointments || [],
    params.patientAppointments || [],
    window.displayStartDate,
    weeksOut,
    params.scheduleOverrides || [],
    { ...(params.options || {}), earliestSlotDate: window.earliestSlotDate },
    params.constraints || null
  );
  const required = (rule.visits_per_week || 0) * weeksOut;
  const coverage = required > 0 ? (placed.length / required) * 100 : 0;
  const isEligible = coverage >= 80;
  const reasons: string[] = [];
  if (!isEligible) {
    reasons.push(`Can place ${placed.length} of ${required} visits (${coverage.toFixed(0)}% coverage, 80% minimum)`);
  }
  return {
    provider_id: params.providerId,
    provider_name: params.providerName || '',
    placed_count: placed.length,
    required_count: required,
    coverage_percentage: Math.round(coverage * 10) / 10,
    is_eligible: isEligible,
    reasons,
  };
}
